/**
 * useTracking Hook - High-level interface for recording sessions.
 *
 * Wraps the tracking store and provides a clean API for the Record page.
 * Bridges between the liveStats shape (runs, vertical, topSpeed, distance,
 * timeSkiing, timeOnLifts) and the legacy stats shape (totalRuns, totalVertical,
 * maxSpeed, totalDistance, activeTime, liftTime) that pages use.
 *
 * @typedef {import('../types/index.js').Session} Session
 * @typedef {import('../types/index.js').SessionStats} SessionStats
 */

import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTrackingStore } from '../stores/useTrackingStore';
import * as sessionsRepo from '../services/db/sessions';
import * as queue from '../services/db/queue';
import { requestSync } from '../services/sync/engine';
import { useActiveUser } from './useActiveUser';

export function useTracking() {
  const store = useTrackingStore();
  const { userId, isAuthed, isAnonymous } = useActiveUser();
  const queryClient = useQueryClient();
  const timerRef = useRef(null);

  const persistSession = useCallback(
    async (session) => {
      if (!session || !userId) return;
      const track = store.currentPoints || [];
      await sessionsRepo.createLocalSession(session, { userId, track });
      if (isAuthed) {
        const { stats, segments, ...rest } = session;
        await queue.enqueue({
          entity: 'session',
          op: 'create',
          recordId: session.id,
          payload: { ...rest, stats, segments, track },
        });
        requestSync({ userId, isAuthed, isAnonymous });
      }
      queryClient.invalidateQueries({ queryKey: ['sessions', userId] });
    },
    [userId, isAuthed, isAnonymous, queryClient, store.currentPoints],
  );

  // ---------------------------------------------------------------------------
  // Elapsed timer fallback. The store's TrackingService already runs its own
  // 1Hz tick during a normal recording session. We only spin up a backup
  // interval if the store hasn't produced any elapsedTime yet AND has a
  // startTime — and we guard the cleanup so we never leave two timers
  // racing each other (the original implementation could double-fire under
  // StrictMode + every render of the effect).
  // ---------------------------------------------------------------------------
  useEffect(() => {
    // Clear any previous timer before deciding whether to start a new one.
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (!store.isRecording || store.isPaused) return undefined;
    if (store.elapsedTime || !store.startTime) return undefined;

    timerRef.current = setInterval(() => {
      if (store.startTime) {
        store.updateElapsed(Date.now() - store.startTime);
      }
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [store.isRecording, store.isPaused, store.startTime, store.elapsedTime]);

  // ---------------------------------------------------------------------------
  // Bridge liveStats to the stats shape pages expect
  // ---------------------------------------------------------------------------
  const liveStats = store.liveStats || {};
  /** @type {SessionStats} */
  const stats = {
    totalRuns: liveStats.runs ?? store.stats?.totalRuns ?? 0,
    totalDistance: liveStats.distance ?? store.stats?.totalDistance ?? 0,
    totalVertical: liveStats.vertical ?? store.stats?.totalVertical ?? 0,
    maxSpeed: liveStats.topSpeed ?? store.stats?.maxSpeed ?? 0,
    avgSpeed: store.stats?.avgSpeed ?? 0,
    totalDuration: store.stats?.totalDuration ?? 0,
    activeTime: liveStats.timeSkiing ?? store.stats?.activeTime ?? 0,
    liftTime: liveStats.timeOnLifts ?? store.stats?.liftTime ?? 0,
    pauseTime: store.stats?.pauseTime ?? 0,
    longestRun: store.stats?.longestRun ?? 0,
    biggestVertical: store.stats?.biggestVertical ?? 0,
  };

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  const start = useCallback(
    (resortId, resortName) => {
      try {
        store.startRecording(resortId, resortName);
      } catch (e) {
        // TrackingService may not be fully wired yet; fall back to basic state
        console.warn('[useTracking] startRecording failed, using fallback:', e.message);
        useTrackingStore.setState({
          isRecording: true,
          isPaused: false,
          startTime: Date.now(),
          elapsed: 0,
          elapsedTime: 0,
          resortId: resortId || null,
          resortName: resortName || null,
        });
      }
    },
    [store],
  );

  const pause = useCallback(() => {
    try {
      store.pauseRecording();
    } catch (e) {
      console.warn('[useTracking] pauseRecording failed:', e.message);
      useTrackingStore.setState({ isPaused: true });
    }
  }, [store]);

  const resume = useCallback(() => {
    try {
      store.resumeRecording();
    } catch (e) {
      console.warn('[useTracking] resumeRecording failed:', e.message);
      useTrackingStore.setState({ isPaused: false });
    }
  }, [store]);

  const stop = useCallback(async () => {
    try {
      const session = await store.stopRecording();
      if (session) {
        await persistSession(session);
      }
      return session;
    } catch (e) {
      console.warn('[useTracking] stopRecording failed, creating fallback session:', e.message);
      const fallbackSession = {
        id: `session_${Date.now()}`,
        resortId: store.resortId,
        resortName: store.resortName,
        startTime: store.startTime || Date.now() - (store.elapsedTime || 0),
        endTime: Date.now(),
        segments: store.segments || [],
        stats,
        status: 'completed',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      await persistSession(fallbackSession);
      useTrackingStore.setState({
        isRecording: false,
        isPaused: false,
        startTime: null,
        elapsed: 0,
        elapsedTime: 0,
      });
      return fallbackSession;
    }
  }, [store, persistSession, stats]);

  // ---------------------------------------------------------------------------
  // Public interface (matches what Record.jsx and other pages expect)
  // ---------------------------------------------------------------------------
  return {
    // State
    isRecording: store.isRecording,
    isPaused: store.isPaused,
    elapsed: store.elapsedTime || store.elapsed || 0,
    startTime: store.startTime,
    currentSpeed: store.currentSpeed,
    currentAltitude: store.currentAltitude,
    currentActivity: store.currentActivity || { type: 'stopped', speed: 0, altitude: 0 },
    resortName: store.resortName,
    stats,
    segments: store.segments || [],

    // Aliases for the specification interface
    recordingState: store.isRecording
      ? store.isPaused
        ? 'paused'
        : 'recording'
      : 'idle',
    currentSession: store.currentSession,
    liveStats: stats,
    elapsedTime: store.elapsedTime || store.elapsed || 0,

    // Actions
    start,
    pause,
    resume,
    stop,

    // Action aliases matching specification
    startRecording: start,
    stopRecording: stop,
    pauseRecording: pause,
    resumeRecording: resume,
  };
}
